import {
    Button,
    Modal,
    ModalCloseButton,
    ModalContent,
    ModalOverlay,
    ModalBody,
    ModalHeader,
    Text,
    VStack,
    useDisclosure,
    Input,
    HStack,
    useToast,
    Spinner,
    Box,
    Heading,
    SimpleGrid,
    Flex,
    Icon,
    FormControl,
    FormLabel,
    InputGroup,
    InputLeftElement,
    useColorModeValue,
    Select as ChakraSelect,
    Table,
    TableContainer,
    Thead,
    Tbody,
    Tr,
    Th,
    Td
} from "@chakra-ui/react";
import React, { useContext, useState, useEffect, useRef } from "react";
import { AppContext } from "../../../context/AppContext";
import {
    AdminCreateCoupons,
    AdminGetAllCoupons,
    DeleteCoupon,
} from "../../../utils/api";
import Empty from "../../../components/Empty";
import CouponCard from "../components/CouponCard";
import { FiPlus, FiTrash2, FiCopy } from "react-icons/fi";

const Coupon = () => {
    const addCouponPriceRef = useRef();
    const { AdminToken } = useContext(AppContext);
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [pageNo, setPageNo] = useState(1);
    const [limitNo, setLimitNo] = useState(10);
    const [totalPages, setTotalPages] = useState(1);
    const toast = useToast();
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [couponId, setCouponId] = useState('');
    const [deleteModal, setDeleteModal] = React.useState(false);

    // Theme Colors
    const cardBg = "gray.800";
    const textColor = "white";
    const subTextColor = "gray.400";
    const borderColor = "gray.700";
    const headerBg = "gray.700";
    const themeColor = "#319795";

    const handleCreateCoupon = (event) => {
        event.preventDefault();
        let couponData = {};
        couponData.couponPrice = addCouponPriceRef.current.value || 0;
        if (!couponData.couponPrice) {
            toast({
                title: `Please Enter the price`,
                status: "warning",
                isClosable: true,
                duration: 3000,
            });
        } else {
            AdminCreateCoupons(couponData, AdminToken)
                .then((res) => {
                    toast({
                        title: `Coupon Created Successfully`,
                        status: "success",
                        isClosable: true,
                        duration: 3000,
                    });
                    data.unshift(res.data.data);
                    setData([...data]);
                    onClose();
                })
                .catch((err) => {
                    toast({
                        title: err.response?.data?.message
                            ? err.response?.data.message
                            : err.message,
                        status: "error",
                        isClosable: true,
                        duration: 3000,
                    });
                });
        }
    };

    useEffect(() => {
        setLoading(false);
        AdminGetAllCoupons(AdminToken, pageNo, limitNo)
            .then((res) => {
                setLoading(true);
                setData(res.data.coupons);
                setTotalPages(res.data.total_pages);
            })
            .catch((err) => {
                setLoading(true);
                console.log(err, "error message");
            });
    }, [limitNo, pageNo, AdminToken]);

    const OpenBlockModal = (couponId) => {
        setCouponId(couponId);
        setDeleteModal(true);
    };

    const CloseBlockModal = () => {
        setDeleteModal(false);
    };

    const handleDelete = () => {
        DeleteCoupon(couponId, AdminToken)
            .then((res) => {
                toast({
                    title: res.data.message,
                    isClosable: true,
                    status: 'success',
                    duration: 3000
                })
                let currentData = data;
                currentData = currentData.filter((el) => el._id !== couponId);
                if (currentData.length === 0) {
                    setPageNo((pre) => { if (pageNo !== 1) { return pre - 1 } return pre });
                }
                setData([...currentData]);
                CloseBlockModal();
            })
            .catch((err) => {
                toast({
                    title: err.response?.data?.message
                        ? err.response?.data.message
                        : err.message,
                    isClosable: true,
                    status: 'error',
                    duration: 3000
                })
            });
    };

    return (
        <Box p={[4, 6]} w="full" minH="100vh">
            <VStack spacing={8} align="stretch">
                {/* Header */}
                <Flex justify="space-between" align="center" wrap="wrap" gap={4}>
                    <Box>
                        <Heading size="lg" color={textColor}>Coupon Management</Heading>
                        <Text color={subTextColor}>Create and manage discount coupons</Text>
                    </Box>
                    <Button
                        leftIcon={<FiPlus />}
                        onClick={onOpen}
                        bg={themeColor}
                        color="white"
                        _hover={{ bg: "teal.600" }}
                        size="md"
                    >
                        Add Coupon
                    </Button>
                </Flex>

                {/* Pagination Controls */}
                {totalPages > 1 && (
                    <Flex justify="flex-end" align="center" gap={2} wrap="wrap">
                        <ChakraSelect
                            w="80px"
                            value={limitNo}
                            onChange={(e) => {
                                setLimitNo(Number(e.target.value));
                                setPageNo(1);
                            }}
                            size="sm"
                            borderRadius="md"
                        >
                            <option value={10}>10</option>
                            <option value={20}>20</option>
                            <option value={40}>40</option>
                            <option value={60}>60</option>
                            <option value={80}>80</option>
                            <option value={100}>100</option>
                        </ChakraSelect>
                        <HStack spacing={2}>
                            <Button size="sm" onClick={() => setPageNo(1)} isDisabled={pageNo === 1}>First</Button>
                            <Button size="sm" onClick={() => setPageNo(p => Math.max(1, p - 1))} isDisabled={pageNo === 1}>{"<"}</Button>
                            <Text fontSize="sm" fontWeight="bold" color={textColor}>{pageNo} / {totalPages}</Text>
                            <Button size="sm" onClick={() => setPageNo(p => Math.min(totalPages, p + 1))} isDisabled={pageNo === totalPages}>{">"}</Button>
                            <Button size="sm" onClick={() => setPageNo(totalPages)} isDisabled={pageNo === totalPages}>Last</Button>
                        </HStack>
                    </Flex>
                )}

                {/* Coupons Table */}
                {!loading ? (
                    <Flex justify="center" py={10}>
                        <Spinner size="xl" color={themeColor} thickness="4px" />
                    </Flex>
                ) : data.length ? (
                    <Box
                        bg={cardBg}
                        borderRadius="lg"
                        boxShadow="sm"
                        borderWidth="1px"
                        borderColor={borderColor}
                        overflow="hidden"
                    >
                        <TableContainer>
                            <Table variant="simple">
                                <Thead bg={headerBg}>
                                    <Tr>
                                        <Th color={subTextColor}>Coupon Code</Th>
                                        <Th color={subTextColor}>Price</Th>
                                        <Th color={subTextColor}>Status</Th>
                                        <Th color={subTextColor}>User</Th>
                                        <Th color={subTextColor}>Action</Th>
                                    </Tr>
                                </Thead>
                                <Tbody>
                                    {data.map((el) => (
                                        <CouponCard
                                            key={el._id}
                                            id={el._id}
                                            couponCode={el.couponCode}
                                            couponPrice={el.couponPrice}
                                            isExpired={el.isExpired}
                                            email={el?.userId?.email || ''}
                                            OpenBlockModal={() => OpenBlockModal(el._id)}
                                            themeColor={themeColor}
                                            textColor={textColor}
                                            subTextColor={subTextColor}
                                        />
                                    ))}
                                </Tbody>
                            </Table>
                        </TableContainer>
                    </Box>
                ) : (
                    <Empty title="No coupons found!" />
                )}
            </VStack>

            {/* Delete Confirmation Modal */}
            <Modal isOpen={deleteModal} onClose={CloseBlockModal} isCentered>
                <ModalOverlay />
                <ModalContent bg="gray.800" color="white">
                    <ModalHeader>Delete Coupon</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody pb={6}>
                        <Text mb={6}>Are you sure you want to delete this coupon? This action cannot be undone.</Text>
                        <HStack spacing={4} justify="flex-end">
                            <Button onClick={CloseBlockModal} variant="ghost" _hover={{ bg: "gray.700" }}>Cancel</Button>
                            <Button colorScheme="red" onClick={handleDelete} leftIcon={<FiTrash2 />}>
                                Delete
                            </Button>
                        </HStack>
                    </ModalBody>
                </ModalContent>
            </Modal>

            {/* Add Coupon Modal */}
            <Modal isOpen={isOpen} onClose={onClose} isCentered>
                <ModalOverlay />
                <ModalContent bg="gray.800" color="white">
                    <ModalHeader>Create New Coupon</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody pb={6}>
                        <form onSubmit={handleCreateCoupon}>
                            <VStack spacing={4}>
                                <FormControl isRequired>
                                    <FormLabel>Coupon Price (₹)</FormLabel>
                                    <InputGroup>
                                        <InputLeftElement children={<Text color="gray.500">₹</Text>} />
                                        <Input
                                            type="number"
                                            placeholder="Enter amount"
                                            ref={addCouponPriceRef}
                                            focusBorderColor={themeColor}
                                            borderColor="gray.600"
                                            _hover={{ borderColor: "gray.500" }}
                                        />
                                    </InputGroup>
                                </FormControl>
                                <Button
                                    type="submit"
                                    bg={themeColor}
                                    color="white"
                                    _hover={{ bg: "teal.600" }}
                                    w="full"
                                    mt={2}
                                >
                                    Create Coupon
                                </Button>
                            </VStack>
                        </form>
                    </ModalBody>
                </ModalContent>
            </Modal>
        </Box>
    );
};

export default Coupon;
